import React, { useEffect, useState } from 'react';
import { managerAPI } from '../../lib/api';
import { Card, CardContent, CardHeader, CardTitle } from '../../components/ui/card';
import { Badge } from '../../components/ui/badge';
import { Button } from '../../components/ui/button';
import { Input } from '../../components/ui/input';
import { Label } from '../../components/ui/label';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../../components/ui/select';
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from '../../components/ui/table';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from '../../components/ui/dialog';
import { DoorOpen, Users, AlertCircle, Loader2, Building2, Plus, Pencil, Trash2, Search, X } from 'lucide-react';
import { toast } from 'sonner';

export default function ManagerUnits() {
  const [units, setUnits] = useState([]);
  const [properties, setProperties] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [permissions, setPermissions] = useState({});
  const [dialogOpen, setDialogOpen] = useState(false);
  const [editingUnit, setEditingUnit] = useState(null);
  const [saving, setSaving] = useState(false);
  const [search, setSearch] = useState('');
  const [statusFilter, setStatusFilter] = useState('all');
  const [form, setForm] = useState({ property_id: '', unit_number: '', floor: 1, capacity: 1, monthly_rent: 0, amenities: '', status: 'vacant' });

  useEffect(() => { loadData(); }, []);

  const loadData = async () => {
    try {
      const [unitsRes, propsRes, dashRes] = await Promise.all([
        managerAPI.getUnits(),
        managerAPI.getProperties(),
        managerAPI.getDashboard()
      ]);
      setUnits(unitsRes.data);
      setProperties(propsRes.data);
      setPermissions(dashRes.data.permissions || {});
    } catch (err) {
      setError(err.response?.data?.detail || 'Failed to load units');
    } finally {
      setLoading(false);
    }
  };

  const openCreate = () => {
    setEditingUnit(null);
    setForm({ property_id: properties[0]?.property_id || '', unit_number: '', floor: 1, capacity: 1, monthly_rent: 0, amenities: '', status: 'vacant' });
    setDialogOpen(true);
  };

  const openEdit = (unit) => {
    setEditingUnit(unit);
    setForm({
      property_id: unit.property_id || unit.property?.property_id || '',
      unit_number: unit.unit_number,
      floor: unit.floor || 1,
      capacity: unit.capacity || 1,
      monthly_rent: unit.monthly_rent || 0,
      amenities: (unit.amenities || []).join(', '),
      status: unit.status || 'vacant'
    });
    setDialogOpen(true);
  };

  const handleSave = async () => {
    if (!form.property_id || !form.unit_number.trim()) {
      toast.error('Property and unit number are required');
      return;
    }
    setSaving(true);
    const payload = { ...form, floor: Number(form.floor), capacity: Number(form.capacity), monthly_rent: Number(form.monthly_rent), amenities: form.amenities ? form.amenities.split(',').map(a => a.trim()).filter(Boolean) : [] };
    try {
      if (editingUnit) {
        await managerAPI.updateUnit(editingUnit.unit_id, payload);
        toast.success('Unit updated');
      } else {
        await managerAPI.createUnit(payload);
        toast.success('Unit created');
      }
      setDialogOpen(false);
      loadData();
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Failed to save unit');
    } finally {
      setSaving(false);
    }
  };

  const handleDelete = async (unit) => {
    if (!window.confirm(`Delete unit ${unit.unit_number}? This cannot be undone.`)) return;
    try {
      await managerAPI.deleteUnit(unit.unit_id);
      toast.success('Unit deleted');
      loadData();
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Failed to delete unit');
    }
  };

  const getStatusBadge = (status) => {
    const styles = {
      vacant: 'bg-green-100 text-green-800',
      occupied: 'bg-blue-100 text-blue-800',
      maintenance: 'bg-yellow-100 text-yellow-800',
    };
    return <Badge className={styles[status] || styles.vacant}>{status?.charAt(0).toUpperCase() + status?.slice(1)}</Badge>;
  };

  if (loading) return <div className="flex items-center justify-center h-64"><Loader2 className="w-8 h-8 animate-spin text-[#C85A42]" /></div>;
  if (error) return <div className="flex flex-col items-center justify-center h-64 text-center"><AlertCircle className="w-12 h-12 text-red-500 mb-4" /><h2 className="text-xl font-semibold mb-2">Access Error</h2><p className="text-muted-foreground">{error}</p></div>;

  const unitsByProperty = units.reduce((acc, unit) => {
    const propName = unit.property?.name || 'Unknown Property';
    if (!acc[propName]) acc[propName] = [];
    acc[propName].push(unit);
    return acc;
  }, {});

  // Apply search + status filter (client-side)
  const query = search.trim().toLowerCase();
  const matchesQuery = (unit, propName) => {
    if (!query) return true;
    const hay = [
      unit.unit_number, propName, unit.status,
      unit.floor, unit.capacity, unit.monthly_rent,
      ...(unit.amenities || []),
    ].filter(Boolean).join(' ').toLowerCase();
    return hay.includes(query);
  };
  const filteredByProperty = Object.entries(unitsByProperty).reduce((acc, [propName, propertyUnits]) => {
    const filtered = propertyUnits.filter(u => matchesQuery(u, propName) && (statusFilter === 'all' || u.status === statusFilter));
    if (filtered.length) acc[propName] = filtered;
    return acc;
  }, {});
  const totalFiltered = Object.values(filteredByProperty).reduce((n, arr) => n + arr.length, 0);

  return (
    <div className="space-y-6" data-testid="manager-units-page">
      <div className="flex items-center justify-between">
        <div>
          <h1 className="text-2xl font-bold">Units</h1>
          <p className="text-muted-foreground">All units you have access to manage</p>
        </div>
        {permissions.create_units && (
          <Button onClick={openCreate} data-testid="create-unit-btn">
            <Plus className="w-4 h-4 mr-2" /> Add Unit
          </Button>
        )}
      </div>

      {units.length > 0 && (
        <div className="flex flex-col sm:flex-row gap-3 items-stretch sm:items-center">
          <div className="relative flex-1">
            <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-muted-foreground" />
            <Input
              value={search}
              onChange={(e) => setSearch(e.target.value)}
              placeholder="Search by unit, property, amenities…"
              className="pl-9 pr-9"
              data-testid="manager-units-search"
            />
            {search && (
              <button
                type="button"
                onClick={() => setSearch('')}
                className="absolute right-3 top-1/2 -translate-y-1/2 text-muted-foreground hover:text-foreground"
                data-testid="manager-units-search-clear"
                aria-label="Clear search"
              >
                <X className="w-4 h-4" />
              </button>
            )}
          </div>
          <Select value={statusFilter} onValueChange={setStatusFilter}>
            <SelectTrigger className="w-full sm:w-44" data-testid="manager-units-status-filter">
              <SelectValue placeholder="Status" />
            </SelectTrigger>
            <SelectContent>
              <SelectItem value="all">All statuses</SelectItem>
              <SelectItem value="vacant">Vacant</SelectItem>
              <SelectItem value="occupied">Occupied</SelectItem>
              <SelectItem value="maintenance">Maintenance</SelectItem>
            </SelectContent>
          </Select>
          {(search || statusFilter !== 'all') && (
            <div className="text-sm text-muted-foreground whitespace-nowrap" data-testid="manager-units-result-count">
              {totalFiltered} of {units.length}
            </div>
          )}
        </div>
      )}

      {units.length === 0 ? (
        <Card><CardContent className="p-12 text-center"><DoorOpen className="w-12 h-12 text-muted-foreground mx-auto mb-4" /><h3 className="font-semibold text-lg mb-2">No Units Available</h3><p className="text-muted-foreground">No units are available in your assigned properties.</p></CardContent></Card>
      ) : totalFiltered === 0 ? (
        <Card><CardContent className="p-12 text-center"><Search className="w-12 h-12 text-muted-foreground mx-auto mb-4" /><h3 className="font-semibold text-lg mb-2">No matches</h3><p className="text-muted-foreground">No units match “{search}”.</p></CardContent></Card>
      ) : (
        Object.entries(filteredByProperty).map(([propertyName, propertyUnits]) => (
          <Card key={propertyName}>
            <CardHeader><CardTitle className="flex items-center gap-2"><Building2 className="w-5 h-5 text-[#C85A42]" />{propertyName}</CardTitle></CardHeader>
            <CardContent>
              <Table>
                <TableHeader>
                  <TableRow>
                    <TableHead>Unit</TableHead>
                    <TableHead>Floor</TableHead>
                    <TableHead>Capacity</TableHead>
                    <TableHead>Rent</TableHead>
                    <TableHead>Status</TableHead>
                    <TableHead>Amenities</TableHead>
                    {(permissions.edit_units) && <TableHead>Actions</TableHead>}
                  </TableRow>
                </TableHeader>
                <TableBody>
                  {propertyUnits.map((unit) => (
                    <TableRow key={unit.unit_id}>
                      <TableCell className="font-medium">{unit.unit_number}</TableCell>
                      <TableCell>{unit.floor}</TableCell>
                      <TableCell><div className="flex items-center gap-1"><Users className="w-4 h-4 text-muted-foreground" />{unit.capacity}</div></TableCell>
                      <TableCell>{unit.monthly_rent?.toLocaleString()}</TableCell>
                      <TableCell>{getStatusBadge(unit.status)}</TableCell>
                      <TableCell>
                        <div className="flex flex-wrap gap-1">
                          {unit.amenities?.slice(0, 2).map((a, i) => <Badge key={i} variant="outline" className="text-xs">{a}</Badge>)}
                          {unit.amenities?.length > 2 && <Badge variant="outline" className="text-xs">+{unit.amenities.length - 2}</Badge>}
                        </div>
                      </TableCell>
                      {(permissions.edit_units) && (
                        <TableCell>
                          <div className="flex gap-1">
                            <Button variant="ghost" size="sm" onClick={() => openEdit(unit)} data-testid={`edit-unit-${unit.unit_id}`}><Pencil className="w-4 h-4" /></Button>
                            <Button variant="ghost" size="sm" className="text-red-500 hover:text-red-700" onClick={() => handleDelete(unit)} data-testid={`delete-unit-${unit.unit_id}`}><Trash2 className="w-4 h-4" /></Button>
                          </div>
                        </TableCell>
                      )}
                    </TableRow>
                  ))}
                </TableBody>
              </Table>
            </CardContent>
          </Card>
        ))
      )}

      <Dialog open={dialogOpen} onOpenChange={setDialogOpen}>
        <DialogContent>
          <DialogHeader><DialogTitle>{editingUnit ? 'Edit Unit' : 'Add Unit'}</DialogTitle></DialogHeader>
          <div className="space-y-4">
            <div>
              <Label>Property *</Label>
              <Select value={form.property_id} onValueChange={v => setForm({ ...form, property_id: v })}>
                <SelectTrigger data-testid="unit-property-select"><SelectValue placeholder="Select property" /></SelectTrigger>
                <SelectContent>{properties.map(p => <SelectItem key={p.property_id} value={p.property_id}>{p.name}</SelectItem>)}</SelectContent>
              </Select>
            </div>
            <div className="grid grid-cols-2 gap-4">
              <div><Label>Unit Number *</Label><Input value={form.unit_number} onChange={e => setForm({ ...form, unit_number: e.target.value })} data-testid="unit-number-input" /></div>
              <div><Label>Floor</Label><Input type="number" value={form.floor} onChange={e => setForm({ ...form, floor: e.target.value })} /></div>
            </div>
            <div className="grid grid-cols-2 gap-4">
              <div><Label>Capacity</Label><Input type="number" value={form.capacity} onChange={e => setForm({ ...form, capacity: e.target.value })} /></div>
              <div><Label>Monthly Rent</Label><Input type="number" value={form.monthly_rent} onChange={e => setForm({ ...form, monthly_rent: e.target.value })} /></div>
            </div>
            <div><Label>Status</Label>
              <Select value={form.status} onValueChange={v => setForm({ ...form, status: v })}>
                <SelectTrigger><SelectValue /></SelectTrigger>
                <SelectContent>
                  <SelectItem value="vacant">Vacant</SelectItem>
                  <SelectItem value="occupied">Occupied</SelectItem>
                  <SelectItem value="maintenance">Maintenance</SelectItem>
                </SelectContent>
              </Select>
            </div>
            <div><Label>Amenities (comma-separated)</Label><Input value={form.amenities} onChange={e => setForm({ ...form, amenities: e.target.value })} placeholder="WiFi, AC, Parking" /></div>
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setDialogOpen(false)}>Cancel</Button>
            <Button onClick={handleSave} disabled={saving} data-testid="save-unit-btn">{saving && <Loader2 className="w-4 h-4 mr-2 animate-spin" />}{editingUnit ? 'Update' : 'Create'}</Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
}
